import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  Receipt,
  AlertCircle,
  TrendingUp,
  Upload as UploadIcon,
  Clock,
} from 'lucide-react';
import { analyticsApi } from '../lib/api';
import { formatCurrency } from '../utils/format';
import { Link } from 'react-router-dom';

export default function Dashboard() {
  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => analyticsApi.getDashboard(30),
  });

  const dashboard = dashboardData?.data?.dashboard;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  const stats = [
    {
      name: 'Total Documents',
      value: dashboard?.overview?.totalDocuments || 0,
      change: `+${dashboard?.overview?.recentDocuments || 0} this month`,
      icon: FileText,
      color: 'blue',
    },
    {
      name: 'Total Receipts',
      value: dashboard?.overview?.totalReceipts || 0,
      change: `+${dashboard?.overview?.recentReceipts || 0} this month`,
      icon: Receipt,
      color: 'green',
    },
    {
      name: 'Total Spending',
      value: formatCurrency(dashboard?.overview?.totalAmount || 0),
      change: `${formatCurrency(dashboard?.overview?.recentAmount || 0)} this month`,
      icon: TrendingUp,
      color: 'purple',
    },
    {
      name: 'Pending Processing',
      value: dashboard?.overview?.pendingProcessing || 0,
      change: 'Awaiting AI processing',
      icon: Clock,
      color: 'yellow',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Overview of your document management system</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="mt-2 text-3xl font-semibold text-gray-900">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">{stat.change}</p>
                </div>
                <div className={`p-3 bg-${stat.color}-100 rounded-lg`}>
                  <Icon className={`h-6 w-6 text-${stat.color}-600`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/upload" className="btn-primary">
            <UploadIcon className="h-5 w-5" />
            Upload Documents
          </Link>
          <Link to="/receipts" className="btn-secondary">
            <Receipt className="h-5 w-5" />
            View Receipts
          </Link>
          <Link to="/reconciliation" className="btn-secondary">
            <AlertCircle className="h-5 w-5" />
            Reconcile Transactions
          </Link>
          <Link to="/search" className="btn-secondary">
            <FileText className="h-5 w-5" />
            Search Documents
          </Link>
        </div>
      </div>

      {/* Recent Activity & Top Vendors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Vendors */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Top Vendors (Last 30 Days)
          </h2>
          {dashboard?.topVendors?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.topVendors.slice(0, 5).map((vendor: any, index: number) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{vendor.name}</p>
                    <p className="text-xs text-gray-500">
                      {vendor.receipt_count} receipt{vendor.receipt_count > 1 ? 's' : ''}
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatCurrency(vendor.total_spent)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No vendor data available</p>
          )}
        </div>

        {/* Top Categories */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Top Categories (Last 30 Days)
          </h2>
          {dashboard?.topCategories?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.topCategories.slice(0, 5).map((category: any, index: number) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{category.name}</p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">
                    {category.document_count} document{category.document_count > 1 ? 's' : ''}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No category data available</p>
          )}
        </div>
      </div>

      {/* Storage Usage */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Storage Usage</h2>
        {dashboard?.storage?.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {dashboard.storage.map((storage: any) => (
              <div key={storage.storage_bucket} className="border rounded-lg p-4">
                <p className="text-sm font-medium text-gray-600 capitalize">
                  {storage.storage_bucket} Storage
                </p>
                <p className="mt-2 text-2xl font-semibold text-gray-900">
                  {storage.count} files
                </p>
                <p className="text-sm text-gray-500">
                  {(storage.total_size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No storage data available</p>
        )}
      </div>
    </div>
  );
}

